import React, { forwardRef, useEffect, useRef } from 'react';
import ReactPaginate from 'react-paginate';
import {
  Cell,
  Column,
  Row,
  SortingRule,
  TableInstance,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import { useSticky } from 'react-table-sticky';
import styled from 'styled-components';

import { colors } from '../../../core';
import Heading from '../Heading';
import Icon from '../Icon';
import Tag from '../Tag';

interface TableProps<T> {
  columns: Column[];
  hiddenColumns: any;
  data: T[];
  onRowClick: (id: string) => void;
  selectedRowId?: string;
  showPagination?: boolean;
  controlledPageCount?: number;
  numberOfRowsToShow?: number;
  onLoadMore?: (pageIndex?: number, pageSize?: number, sortBy?: SortingRule<object>[]) => void;
  removeFilters?: boolean;
  manualPagination?: boolean;
  manualSortBy?: boolean;
  showSelectColum?: boolean;
  onClickMergeButton?: (instance: TableInstance) => void;
  rowProps?: (row: any) => any;
  suspendOnClickInThisColumns?: string[];
  autoResetPage?: boolean;
}

const TableFrame = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const TableWrapper = styled.table`
  border-collapse: collapse;
  border-spacing: 0;
  width: 100%;
  font-family: 'Quicksand', 'sans-serif';
`;

const TableHead = styled.thead`
  border-bottom: 1px solid ${colors.cloud};
`;

const TableHeadRow = styled.tr``;

const TableBody = styled.tbody``;

const TableBodyRow = styled.tr<{ selected: any }>`
  cursor: pointer;

  &:nth-child(odd) {
    background-color: ${colors.white};
  }

  &:nth-child(even) {
    background-color: ${colors.pearl};
  }

  &:hover {
    background-color: ${colors.smoke};
  }

  ${({ selected }) =>
    selected &&
    `
    && {
      background-color: ${colors.primaryOpacity};
    }
  `}
`;

const TableHeader = styled.th`
  color: ${colors.charcoal};
  padding: 16px 20px;
  text-align: left;
`;

const TableCell = styled.td<{
  fixedWidth?: number;
  noPadding?: boolean;
  selected?: boolean;
}>`
  color: ${colors.ash};
  font-size: 14px;

  padding: ${({ noPadding }) => (noPadding ? '0 20px' : '20px 20px')};
  text-align: left;

  ${({ fixedWidth }) =>
    fixedWidth &&
    `
    width: ${fixedWidth}px;
  `}

  ${Tag} {
    margin-top: 5px;
  }

  ${({ selected }) =>
    selected &&
    `
    && {
      color: ${colors.darkgray};
    }
  `}
`;

const PaginationWrapper = styled.div`
  margin: 0 auto;
  max-width: 600px;
  padding: 0;

  .pagination {
    padding: 20px;
    display: flex;
    justify-content: space-between;
    list-style: none;
    cursor: pointer;
  }

  .page,
  .break {
    color: ${colors.primary};
  }

  a:focus {
    outline: 0;
  }

  .link {
    padding: 3px 10px;
    border-radius: 3px;
  }

  .active a {
    color: ${colors.ash};
    background: ${colors.primaryOpacity};
    border: none;
    outline: 0;
  }
`;

const RowContainer = styled.div`
  align-items: center;
  display: flex;
`;

const IconWrapper = styled.div<{ removeFilters: boolean }>`
  visibility: ${({ removeFilters }) => (removeFilters ? 'hidden' : 'visible')};
  margin-left: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledIcon = styled(Icon)`
  cursor: pointer;
`;

const CheckBox = styled.input`
  -webkit-appearance: none;
  appearance: none;
  background-color: #fff;
  margin: 0;
  font: inherit;
  width: 1.15em;
  height: 1.15em;
  border-radius: 3px;
  background-color: ${({ theme }) => theme.checkboxBackgroundColor || colors.white};
  border: 1px solid ${({ theme }) => theme.checkboxBorderColor || colors.smoke};
  transform: translateY(-0.075em);
  display: grid;
  place-content: center;

  &:before {
    content: '';
    width: 0.65em;
    height: 0.65em;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    transform-origin: bottom left;
    clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
    box-shadow: inset 1em 1em #fafafa;
  }

  &:checked {
    background-color: ${({ theme }) => theme.checkboxBackgroundActiveColor || colors.primary};
    &:before {
      transform: scale(1);
    }
  }

  &:focus,
  &:hover {
    border: 1px solid ${({ theme }) => theme.checkboxHover || colors.primaryHover};
    box-shadow: 0 0 3px ${({ theme }) => theme.checkboxShadow || colors.primaryHover};
  }
`;

const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }: any, ref: any) => {
  const defaultRef = useRef();
  const resolvedRef = ref || defaultRef;

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return <CheckBox type="checkbox" ref={resolvedRef} {...rest} />;
});

const Table = <T extends {}>({
  columns,
  data,
  hiddenColumns = [],
  numberOfRowsToShow = 0,
  onRowClick,
  showPagination,
  selectedRowId,
  removeFilters = false,
  manualPagination = false,
  onLoadMore,
  controlledPageCount = 0,
  manualSortBy = false,
  showSelectColum = false,
  onClickMergeButton,
  rowProps,
  suspendOnClickInThisColumns = [],
  autoResetPage = false,
}: TableProps<T>) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        hiddenColumns: hiddenColumns,
        pageSize: numberOfRowsToShow,
      },
      manualPagination,
      manualSortBy,
      pageCount: controlledPageCount,
      autoResetPage: autoResetPage,
      autoResetSortBy: false,
    },
    useSortBy,
    usePagination,
    useSticky,
    useRowSelect,
    (hooks) => {
      if (showSelectColum) {
        hooks.visibleColumns.push((columns, { instance }) => [
          {
            id: 'selection',
            Header: () => (
              <Icon
                type="merge"
                color={colors.primary}
                onClick={() => onClickMergeButton && onClickMergeButton(instance)}
              />
            ),
            Cell: ({ row }: any) => <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />,
            width: 1,
            disableSortBy: true,
          },
          ...columns,
        ]);
      }
    }
  );
  const firstUpdate = useRef(true);

  useEffect(() => {
    if (onLoadMore && !firstUpdate.current) {
      onLoadMore(pageIndex, pageSize, sortBy);
    } else {
      firstUpdate.current = false;
    }
  }, [pageIndex, pageSize, sortBy, onLoadMore]);

  const handleRowOnClick = (event: any, row: any) => {
    const getCurrentCellIndex = event.target.cellIndex;

    // If we are going to use the select column then we disable the onClick of the first column
    // checking that the index of the current cell is different to the one of the initial column
    if (showSelectColum) {
      const getLastCellIndex =
        document &&
        document.querySelector<HTMLTableCellElement>('table tr:first-child td:first-child')
          ?.cellIndex;

      if (getCurrentCellIndex !== getLastCellIndex && getCurrentCellIndex !== undefined) {
        onRowClick(row.values.id);
      }
    } else {
      // If we want to suspend the onClick of a different column we add the id of the column to the parameter
      // suspendOnClickInThisColumns, then we get the index of the columns to suspend the event and then we ask
      // to see if the current index is part of the suspended list, if this list doesn't have a item the we do the
      // normal event
      if (suspendOnClickInThisColumns.length > 0) {
        const listOfIndexToSuspendClick = suspendOnClickInThisColumns.map((columId) => {
          return (
            document && document.querySelector<HTMLTableCellElement>(`#cell_${columId}`)?.cellIndex
          );
        });

        const cellName = event?.nativeEvent?.composedPath()?.find((path: any) =>
          path.id.includes(`cell_`)
        )?.cellIndex;

        if (!listOfIndexToSuspendClick.includes(cellName)) {
          onRowClick(row.values.id);
        }
      } else {
        onRowClick(row.values.id);
      }
    }
  };

  return (
    <TableFrame>
      <TableWrapper {...getTableProps()} className="table sticky">
        <TableHead>
          {headerGroups.map((headerGroup, index) => (
            <TableHeadRow {...headerGroup.getHeaderGroupProps()} className="tr" key={index}>
              {headerGroup.headers.map((column: any, i: number) => {
                return (
                  <TableHeader
                    key={i}
                    id={column.id}
                    className="th"
                    width={column.width}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    <RowContainer>
                      <Heading smallCaps noMargin>
                        {column.render('Header')}
                      </Heading>

                      {column.id !== 'selection' ? (
                        <IconWrapper removeFilters={removeFilters}>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <StyledIcon type={'caret-up'} color={colors.primary} size={12} />
                            ) : (
                              <StyledIcon type={'caret-down'} color={colors.primary} size={12} />
                            )
                          ) : (
                            <StyledIcon
                              type={'order'}
                              color={column.canSort ? colors.primary : colors.primaryDisabled}
                              size={12}
                              viewSize={12}
                            />
                          )}
                        </IconWrapper>
                      ) : null}
                    </RowContainer>
                  </TableHeader>
                );
              })}
            </TableHeadRow>
          ))}
        </TableHead>

        <TableBody {...getTableBodyProps()} className="body">
          {page.map((row: Row) => {
            prepareRow(row);

            const isSelected = row.values.id === selectedRowId;

            return (
              <TableBodyRow
                {...row.getRowProps()}
                {...(rowProps && row.getRowProps(rowProps(row)))}
                onClick={(event) => handleRowOnClick(event, row)}
                className="tr"
                selected={isSelected}
                key={row.values.id}
              >
                {row.cells.map((cell: Cell, index: number) => {
                  return (
                    <TableCell
                      {...cell.getCellProps()}
                      className="td"
                      selected={isSelected}
                      key={`${cell.value}-${index}`}
                      id={`cell_${cell.column.id}`}
                    >
                      {cell.value || cell.column.id === 'selection' ? cell.render('Cell') : '-'}
                    </TableCell>
                  );
                })}
              </TableBodyRow>
            );
          })}
        </TableBody>
      </TableWrapper>

      {showPagination && (
        <PaginationWrapper>
          <ReactPaginate
            containerClassName={'pagination'}
            pageClassName={'page'}
            pageLinkClassName={'link'}
            activeClassName="active"
            pageCount={pageCount}
            pageRangeDisplayed={5}
            marginPagesDisplayed={2}
            nextLabel={
              <StyledIcon
                type={'caret-right'}
                color={!canNextPage ? colors.primaryDisabled : colors.primary}
              />
            }
            previousLabel={
              <StyledIcon
                type={'caret-left'}
                color={!canPreviousPage ? colors.primaryDisabled : colors.primary}
              />
            }
            onPageChange={({ selected }) => gotoPage(selected)}
            initialPage={pageIndex}
          />
        </PaginationWrapper>
      )}
    </TableFrame>
  );
};

export default Table;
